// Guide §7.1 + Bible 3.0, 7.0, 13.3, 13.7, 18.3 — the ONE place where category differences live.
// Rule (Guide §7.1): no category-based `if` anywhere in the code base that is not driven by this config.
import type { Category } from './enums';

export type CategoryFamily = 'retail' | 'service' | 'food' | 'agri' | 'financial' | 'personal';
export type ReleaseStage = 'R1.0' | 'R1.1' | 'R1.2';
export type VendorModule = 'required' | 'optional' | 'hidden';

export type Tab2 = 'baki' | 'cash_in_out' | 'income_expense';
export type Tab4 = 'stock' | 'balance' | 'property' | 'project' | 'crop' | 'order' | 'menu' | 'savings';

export type Fab =
  | 'new_sale'
  | 'new_cash_txn'
  | 'new_order'
  | 'new_client_or_lead'
  | 'new_invoice'
  | 'new_crop_sale'
  | 'new_order_with_measure'
  | 'new_income_or_expense';

export interface CategoryTool {
  key: string;
  bn: string;
  en: string;
  /** ★ in Bible 3.0: the single feature that is open on the Basic plan */
  basic?: boolean;
}

export interface CategoryConfigEntry {
  family: CategoryFamily;
  release: ReleaseStage;
  labelBn: string;
  labelEn: string;
  tab2: Tab2;
  tab4: Tab4;
  tab4Segments: readonly string[];
  fab: Fab;
  vendorModule: VendorModule;
  zakatInScope: boolean;
  /** Key of the ★ Basic feature (must exist in `tools`) */
  basicFeature: string | null;
  color: string;
  icon: string;
  tools: readonly CategoryTool[];
  /** Does this category sell stock-carrying products? Used by DukanIQ Tier A and expiry nudges */
  hasExpiry?: boolean;
}

const t = (key: string, bn: string, en: string, basic = false): CategoryTool => ({ key, bn, en, basic });

export const CATEGORY_CONFIG: Record<Category, CategoryConfigEntry> = {
  clothing: {
    family: 'retail', release: 'R1.0', labelBn: 'পোশাক ও ফ্যাশন', labelEn: 'Clothing & Fashion',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'variant', 'season_design'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'size_color_variants', color: '#C026D3', icon: 'shirt',
    tools: [
      t('size_color_variants', 'সাইজ ও কালার ভ্যারিয়েন্ট', 'Size & colour variants', true),
      t('season_tags', 'সিজনাল কালেকশন ট্যাগ', 'Seasonal collection tags'),
      t('style_code', 'ডিজাইন/স্টাইল কোড', 'Design / style code'),
    ],
  },
  departmental: {
    family: 'retail', release: 'R1.0', labelBn: 'ডিপার্টমেন্টাল স্টোর', labelEn: 'Departmental Store',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'section', 'rack'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'multi_category_sections', color: '#4F46E5', icon: 'store',
    tools: [
      t('multi_category_sections', 'মাল্টি-ক্যাটাগরি সেকশন', 'Multi-category sections', true),
      t('rack_location', 'শেলফ ও র‍্যাক লোকেশন', 'Shelf & rack location'),
    ],
  },
  electronics: {
    family: 'retail', release: 'R1.0', labelBn: 'ইলেকট্রনিক্স ও মোবাইল', labelEn: 'Electronics & Mobile',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'imei', 'service_warranty'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'imei_tracking', color: '#2563EB', icon: 'smartphone',
    tools: [
      t('imei_tracking', 'IMEI / সিরিয়াল ট্র্যাকিং', 'IMEI / serial tracking', true),
      t('warranty', 'ওয়ারেন্টি তারিখ', 'Warranty tracking'),
      t('repair_log', 'সার্ভিস ও রিপেয়ার লগ', 'Service & repair log'),
      t('installments', 'EMI / কিস্তিতে বিক্রি', 'EMI / instalment sales'),
    ],
  },
  general: {
    family: 'retail', release: 'R1.0', labelBn: 'জেনারেল স্টোর', labelEn: 'General Store',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'tags'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'sub_category_tags', color: '#0D9488', icon: 'basket',
    tools: [t('sub_category_tags', 'নিজের মতো সাব-ক্যাটাগরি ট্যাগ', 'Custom sub-category tags', true)],
  },
  grocery: {
    family: 'retail', release: 'R1.0', labelBn: 'গ্রোসারি স্টোর', labelEn: 'Grocery Store',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'batch_expiry'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true, hasExpiry: true,
    basicFeature: 'batch_expiry_alert', color: '#16A34A', icon: 'leaf',
    tools: [
      t('batch_expiry_alert', 'ব্যাচভিত্তিক মেয়াদের সতর্কতা', 'Batch expiry alerts', true),
      t('weighted_pricing', 'ওজনে দাম (কেজি, গ্রাম)', 'Weight-based pricing (kg, g)'),
    ],
  },
  hardware: {
    family: 'retail', release: 'R1.0', labelBn: 'হার্ডওয়্যার ও টুলস', labelEn: 'Hardware & Tools',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'unit_bulk'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'unit_conversion', color: '#D97706', icon: 'wrench',
    tools: [
      t('unit_conversion', 'ইউনিট কনভার্সন (পিস, ডজন, বক্স)', 'Unit conversion (pcs, dozen, box)', true),
      t('bulk_tier', 'পরিমাণভিত্তিক দাম (Bulk)', 'Quantity-based price (bulk tier)'),
    ],
  },
  mobile_banking: {
    family: 'financial', release: 'R1.1', labelBn: 'মোবাইল ব্যাংকিং ও রিচার্জ', labelEn: 'Mobile Banking & Recharge',
    tab2: 'cash_in_out', tab4: 'balance', tab4Segments: ['balance', 'commission', 'recharge'],
    fab: 'new_cash_txn', vendorModule: 'hidden', zakatInScope: false,
    basicFeature: 'multi_mfs_balance', color: '#7C3AED', icon: 'card',
    tools: [
      t('multi_mfs_balance', 'প্রোভাইডারভিত্তিক ব্যালেন্স', 'Multi-MFS balances', true),
      t('commission_report', 'কমিশন রিপোর্ট', 'Commission report'),
      t('float_alert', 'ফ্লোট সতর্কতা', 'Float alerts'),
      t('recharge_log', 'রিচার্জ লগ', 'Recharge log'),
    ],
  },
  personal_savings: {
    family: 'personal', release: 'R1.1', labelBn: 'ব্যক্তিগত সঞ্চয়', labelEn: 'Personal Savings',
    tab2: 'income_expense', tab4: 'savings', tab4Segments: ['accounts', 'goals'],
    fab: 'new_income_or_expense', vendorModule: 'hidden', zakatInScope: false,
    basicFeature: null, color: '#CA8A04', icon: 'moneybag',
    tools: [],
  },
  pharmacy: {
    family: 'retail', release: 'R1.0', labelBn: 'ফার্মেসি', labelEn: 'Pharmacy & Drugstore',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['medicine', 'batch_expiry', 'generic'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true, hasExpiry: true,
    basicFeature: 'expiry_alert', color: '#DC2626', icon: 'capsule',
    tools: [
      t('expiry_alert', 'মেয়াদের সতর্কতা', 'Expiry alerts', true),
      t('batch_no', 'ব্যাচ নম্বর', 'Batch numbers'),
      t('generic_map', 'Generic ও Brand ম্যাপিং', 'Generic ↔ brand mapping'),
      t('medicine_autofill', 'Medicine Database ও Autofill', 'Medicine database & autofill'),
      t('unit_conversion', 'ইউনিট কনভার্সন (বক্স, পাতা, পিস)', 'Unit conversion (box, strip, piece)'),
    ],
  },
  restaurant: {
    family: 'food', release: 'R1.1', labelBn: 'রেস্তোরাঁ ও ক্যাফে', labelEn: 'Restaurant & Cafe',
    tab2: 'baki', tab4: 'menu', tab4Segments: ['menu', 'table', 'kitchen'],
    fab: 'new_order', vendorModule: 'optional', zakatInScope: false,
    basicFeature: 'table_order', color: '#C2410C', icon: 'utensils',
    tools: [
      t('table_order', 'টেবিল ও অর্ডার ম্যানেজমেন্ট', 'Tables & orders', true),
      t('digital_menu', 'ডিজিটাল মেনু', 'Digital menu'),
      t('kitchen_status', 'কিচেন স্ট্যাটাস', 'Kitchen status'),
    ],
  },
  real_estate: {
    family: 'service', release: 'R1.2', labelBn: 'রিয়েল এস্টেট', labelEn: 'Real Estate',
    tab2: 'baki', tab4: 'property', tab4Segments: ['property', 'lead', 'installment_commission'],
    fab: 'new_client_or_lead', vendorModule: 'hidden', zakatInScope: false,
    basicFeature: 'property_list', color: '#1E3A8A', icon: 'building',
    tools: [
      t('property_list', 'প্রপার্টি তালিকা', 'Property listing', true),
      t('leads', 'Client ও Lead ফলো-আপ', 'Client & lead follow-up'),
      t('installments', 'কিস্তির পরিকল্পনা', 'Instalment plans'),
      t('agent_commission', 'এজেন্ট কমিশন', 'Agent commission'),
    ],
  },
  software: {
    family: 'service', release: 'R1.2', labelBn: 'সফটওয়্যার / আইটি কোম্পানি', labelEn: 'Software / IT Company',
    tab2: 'baki', tab4: 'project', tab4Segments: ['project', 'retainer'],
    fab: 'new_invoice', vendorModule: 'hidden', zakatInScope: false,
    basicFeature: 'project_milestones', color: '#0891B2', icon: 'laptop',
    tools: [
      t('project_milestones', 'প্রজেক্ট ও মাইলস্টোন', 'Projects & milestones', true),
      t('client_invoicing', 'ক্লায়েন্ট ইনভয়েসিং', 'Client invoicing'),
      t('retainer', 'মাসিক Retainer', 'Monthly retainer'),
    ],
  },
  agriculture: {
    family: 'agri', release: 'R1.2', labelBn: 'কৃষি ও এগ্রো', labelEn: 'Agriculture & Agro',
    tab2: 'baki', tab4: 'crop', tab4Segments: ['crop', 'land', 'expense'],
    fab: 'new_crop_sale', vendorModule: 'optional', zakatInScope: false,
    basicFeature: 'crop_season', color: '#854D0E', icon: 'wheat',
    tools: [
      t('crop_season', 'ফসল ও মৌসুম', 'Crops & seasons', true),
      t('land_parcels', 'জমির খণ্ড', 'Land parcels'),
      t('input_costs', 'ইনপুট খরচ', 'Input costs'),
      t('season_reminders', 'মৌসুমের রিমাইন্ডার', 'Season reminders'),
    ],
  },
  wholesale: {
    family: 'retail', release: 'R1.0', labelBn: 'হোলসেল ও ডিস্ট্রিবিউটর', labelEn: 'Wholesale & Distributor',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'price_tier'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'dealer_retailer_credit', color: '#57534E', icon: 'box',
    tools: [
      t('dealer_retailer_credit', 'Dealer ও Retailer-এর আলাদা ক্রেডিট', 'Separate dealer / retailer credit', true),
      t('price_tiers', 'তিন স্তরের দাম (Dealer, Retailer, Consumer)', 'Three price tiers'),
    ],
  },
  tailoring: {
    family: 'service', release: 'R1.1', labelBn: 'টেইলারিং ও বুটিক', labelEn: 'Tailoring & Boutique',
    tab2: 'baki', tab4: 'order', tab4Segments: ['order', 'measurement', 'delivery'],
    fab: 'new_order_with_measure', vendorModule: 'optional', zakatInScope: false,
    basicFeature: 'measurement_records', color: '#BE185D', icon: 'spool',
    tools: [
      t('measurement_records', 'মাপের রেকর্ড', 'Measurement records', true),
      t('orders_delivery', 'অর্ডার ও ডেলিভারি', 'Orders & delivery'),
      t('advance_payment', 'অগ্রিম টাকা', 'Advance payments'),
    ],
  },
  stationary: {
    family: 'retail', release: 'R1.0', labelBn: 'স্টেশনারি', labelEn: 'Stationary',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'service_log', 'bundle'],
    fab: 'new_sale', vendorModule: 'required', zakatInScope: true,
    basicFeature: 'service_log', color: '#65A30D', icon: 'pencil',
    tools: [
      t('service_log', 'প্রিন্ট, ফটোকপি, লেমিনেশন লগ', 'Print / photocopy / lamination log', true),
      t('seasonal_bulk', 'মৌসুমি Bulk দাম', 'Seasonal bulk pricing'),
      t('bundles', 'Class অনুযায়ী বই-খাতার বান্ডেল', 'Class-wise book bundles'),
      t('season_alerts', 'ভর্তি ও পরীক্ষার মৌসুমের সতর্কতা', 'Admission & exam season alerts'),
    ],
  },
  others: {
    family: 'retail', release: 'R1.0', labelBn: 'অন্যান্য', labelEn: 'Others / Generic',
    tab2: 'baki', tab4: 'stock', tab4Segments: ['product', 'custom_fields'],
    fab: 'new_sale', vendorModule: 'optional', zakatInScope: false,
    basicFeature: 'custom_fields_tags', color: '#64748B', icon: 'folder',
    tools: [t('custom_fields_tags', 'নিজের কাস্টম ফিল্ড ও ট্যাগ', 'Custom fields & tags', true)],
  },
} as const;

/** The ten categories available at public launch (Bible 1.2, DEC-25). */
export const R10_CATEGORIES = (Object.keys(CATEGORY_CONFIG) as Category[]).filter(
  (c) => CATEGORY_CONFIG[c].release === 'R1.0',
);

/** Is the category live in this build? All 17 are (R1.0 + R1.1 + R1.2). A category dropped from this list shows "শীঘ্রই" and joins the waitlist again. */
export const LIVE_RELEASES: readonly ReleaseStage[] = ['R1.0', 'R1.1', 'R1.2'];
export const isCategoryLive = (c: Category): boolean => LIVE_RELEASES.includes(CATEGORY_CONFIG[c].release);

/** The nine Zakat-in-scope "trading" categories (Bible 14.2). */
export const ZAKAT_CATEGORIES = (Object.keys(CATEGORY_CONFIG) as Category[]).filter(
  (c) => CATEGORY_CONFIG[c].zakatInScope,
);

/** Order shown on the signup category picker (Bible 18.1 step 6): the 17 icons. */
export const CATEGORY_ORDER: readonly Category[] = [
  'general', 'grocery', 'clothing', 'pharmacy', 'electronics', 'hardware', 'departmental', 'stationary', 'wholesale', 'others',
  'mobile_banking', 'restaurant', 'tailoring', 'personal_savings', 'real_estate', 'software', 'agriculture',
];

/** A customer may correct a wrong category by themselves this many times per shop (signup mistake). After that only an admin can change it. */
export const CATEGORY_SELF_CHANGES = 1;

/** DEC-30: switching category is free inside a family; across families only before the first transaction. */
export function canChangeCategory(from: Category, to: Category, hasTransactions: boolean): boolean {
  if (from === to) return true;
  const a = CATEGORY_CONFIG[from].family;
  const b = CATEGORY_CONFIG[to].family;
  if (a === 'personal' || b === 'personal') return false; // Personal Space never converts (Bible 18.3)
  if (a === b) return true;
  return !hasTransactions;
}

/** Bible 8.0: Basic = the one ★ tool, Pro/Business/Enterprise = all tools. Free = none. */
export function categoryToolAllowed(plan: 'free' | 'basic' | 'pro' | 'business' | 'enterprise', category: Category, toolKey: string): boolean {
  if (plan === 'free') return false;
  const tool = CATEGORY_CONFIG[category].tools.find((x) => x.key === toolKey);
  if (!tool) return false;
  if (plan === 'basic') return tool.basic === true;
  return true;
}

export const categoryLabel = (c: Category, lang: 'bn' | 'en' = 'bn'): string =>
  lang === 'bn' ? CATEGORY_CONFIG[c].labelBn : CATEGORY_CONFIG[c].labelEn;

// Names of the bottom-bar tabs by key (Bible 13.2/13.3)
export const TAB2_LABEL: Record<Tab2, { bn: string; en: string }> = {
  baki: { bn: 'বাকি খাতা', en: 'Baki' },
  cash_in_out: { bn: 'ক্যাশ ইন/আউট', en: 'Cash In/Out' },
  income_expense: { bn: 'আয়-ব্যয়', en: 'Income & expense' },
};
export const TAB4_LABEL: Record<Tab4, { bn: string; en: string }> = {
  stock: { bn: 'স্টক', en: 'Stock' },
  balance: { bn: 'ব্যালেন্স', en: 'Balance' },
  property: { bn: 'প্রপার্টি', en: 'Property' },
  project: { bn: 'প্রজেক্ট', en: 'Projects' },
  crop: { bn: 'ফসল', en: 'Crops' },
  order: { bn: 'অর্ডার', en: 'Orders' },
  menu: { bn: 'মেনু', en: 'Menu' },
  savings: { bn: 'সঞ্চয়', en: 'Savings' },
};
export const FAB_LABEL: Record<Fab, { bn: string; en: string }> = {
  new_sale: { bn: 'নতুন বিক্রি', en: 'New sale' },
  new_cash_txn: { bn: 'নতুন Cash-In/Out', en: 'New cash txn' },
  new_order: { bn: 'নতুন অর্ডার', en: 'New order' },
  new_client_or_lead: { bn: 'নতুন Client/Lead', en: 'New client/lead' },
  new_invoice: { bn: 'নতুন Invoice', en: 'New invoice' },
  new_crop_sale: { bn: 'নতুন ফসল বিক্রি', en: 'New crop sale' },
  new_order_with_measure: { bn: 'নতুন অর্ডার (মাপসহ)', en: 'New order (with measurements)' },
  new_income_or_expense: { bn: 'নতুন আয় বা খরচ', en: 'New income/expense' },
};
export const SEGMENT_LABEL: Record<string, { bn: string; en: string }> = {
  product: { bn: 'পণ্য', en: 'Products' },
  variant: { bn: 'ভ্যারিয়েন্ট', en: 'Variants' },
  season_design: { bn: 'সিজন ও ডিজাইন কোড', en: 'Season & design code' },
  section: { bn: 'সেকশন', en: 'Sections' },
  rack: { bn: 'র‍্যাক', en: 'Racks' },
  imei: { bn: 'IMEI', en: 'IMEI' },
  service_warranty: { bn: 'সার্ভিস ও ওয়ারেন্টি', en: 'Service & warranty' },
  medicine: { bn: 'ওষুধ', en: 'Medicines' },
  batch_expiry: { bn: 'ব্যাচ ও মেয়াদ', en: 'Batch & expiry' },
  generic: { bn: 'জেনেরিক', en: 'Generic' },
  service_log: { bn: 'সার্ভিস লগ', en: 'Service log' },
  bundle: { bn: 'বান্ডেল', en: 'Bundles' },
  tags: { bn: 'ট্যাগ', en: 'Tags' },
  unit_bulk: { bn: 'ইউনিট ও বাল্ক দাম', en: 'Units & bulk price' },
  price_tier: { bn: 'দামের স্তর', en: 'Price tiers' },
  custom_fields: { bn: 'কাস্টম ফিল্ড', en: 'Custom fields' },
  balance: { bn: 'ব্যালেন্স', en: 'Balance' },
  commission: { bn: 'কমিশন', en: 'Commission' },
  recharge: { bn: 'রিচার্জ', en: 'Recharge' },
  menu: { bn: 'মেনু', en: 'Menu' },
  table: { bn: 'টেবিল', en: 'Tables' },
  kitchen: { bn: 'কিচেন', en: 'Kitchen' },
  order: { bn: 'অর্ডার', en: 'Orders' },
  measurement: { bn: 'মাপ', en: 'Measurements' },
  delivery: { bn: 'ডেলিভারি', en: 'Delivery' },
  property: { bn: 'প্রপার্টি', en: 'Properties' },
  lead: { bn: 'লিড', en: 'Leads' },
  installment_commission: { bn: 'কিস্তি ও কমিশন', en: 'Instalments & commission' },
  project: { bn: 'প্রজেক্ট', en: 'Projects' },
  retainer: { bn: 'রিটেইনার', en: 'Retainers' },
  crop: { bn: 'ফসল', en: 'Crops' },
  land: { bn: 'জমি', en: 'Land' },
  expense: { bn: 'খরচ', en: 'Costs' },
  accounts: { bn: 'সঞ্চয়ের জায়গা', en: 'Accounts' },
  goals: { bn: 'লক্ষ্য', en: 'Goals' },
};
